<template>
	<div class="tw-flex tw-h-full tw-flex-col tw-gap-6 tw-p-6" :dir="isRTL ? 'rtl' : 'ltr'">
		<!-- Header: Title + Toggle -->
		<div class="tw-flex tw-items-center tw-justify-between">
			<div class="tw-text-2xl tw-font-semibold tw-text-gray-900">
				{{ $t('integrations.instagram-settings') }}
			</div>
			<div class="tw-flex tw-items-center tw-gap-4">
				<span class="tw-text-base tw-font-normal tw-text-black">
					{{ $t('integrations.instagram-manual-connection') }}
				</span>
				<AppToggle v-model="manualMode" />
			</div>
		</div>

		<!-- Manual Mode: 4 fields + confirm button (shown when toggle is ON) -->
		<template v-if="manualMode">
			<AppForm @submit="onSubmitInstagram" class="tw-mt-2">
				<div class="tw-grid tw-grid-cols-2 tw-gap-x-3">
					<div class="tw-col-span-2 lg:tw-col-span-1">
						<AppInput
							:label="$t('integrations.account-id')"
							v-model="form.ig_account_id"
							:rules="['required']"
							class="tw-w-full"
						/>
					</div>
					<div class="tw-col-span-2 lg:tw-col-span-1">
						<AppInput
							:label="$t('integrations.access-token')"
							v-model="form.ig_access_token"
							:rules="['required']"
							class="tw-w-full"
						/>
					</div>
					<div class="tw-col-span-2 lg:tw-col-span-1">
						<AppInput
							:label="$t('integrations.app-id')"
							v-model="form.ig_app_id"
							:rules="['required']"
							class="tw-w-full"
						/>
					</div>
					<div class="tw-col-span-2 lg:tw-col-span-1">
						<AppInput
							:label="$t('integrations.app-secret')"
							v-model="form.ig_app_secret"
							:rules="['required']"
							class="tw-w-full"
						/>
					</div>
				</div>
				<div class="tw-mt-5 tw-flex tw-items-center tw-justify-start tw-gap-3">
					<AppButton
						:label="$t('integrations.confirm-data')"
						size="lg"
						type="submit"
						:loading="submitLoading"
						endIcon="arrow-narrow-right"
					/>
				</div>
			</AppForm>
		</template>

		<!-- Default Mode: Description + Steps + Connect Button (shown when toggle is OFF) -->
		<template v-else>
			<!-- Description Section -->
			<div class="tw-flex tw-flex-col tw-gap-1">
				<div class="tw-w-full tw-text-lg tw-font-medium tw-text-gray-900">
					{{ $t('integrations.instagram-link-title') }}
				</div>
				<div class="tw-w-full tw-text-sm tw-font-normal tw-text-gray-600">
					{{ $t('integrations.instagram-link-description') }}
				</div>
			</div>

			<!-- Requirements Steps Card -->
			<div class="tw-flex tw-flex-col tw-gap-3 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-4">
				<div class="tw-text-sm tw-font-semibold tw-text-gray-900">
					{{ $t('integrations.instagram-steps-title') }}
				</div>

				<!-- Step 1 -->
				<div class="tw-flex tw-w-full tw-items-center tw-gap-2.5">
					<div class="tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-gray-200 tw-bg-white">
						<span class="tw-text-sm tw-font-semibold tw-text-gray-900">1</span>
					</div>
					<div class="tw-flex tw-flex-1 tw-flex-col tw-gap-1">
						<span class="tw-text-xs tw-font-normal tw-text-gray-800">
							{{ $t('integrations.instagram-step-1-text') }}
							<a
								href="https://help.instagram.com/502981923235522/?helpref=uf_share"
								target="_blank"
								rel="noopener noreferrer"
								class="tw-font-semibold tw-text-brand-500 hover:tw-text-brand-600 tw-underline"
							>{{ $t('integrations.instagram-step-1-link') }}</a>
						</span>
					</div>
				</div>

				<!-- Step 2 -->
				<div class="tw-flex tw-w-full tw-items-center tw-gap-2.5">
					<div class="tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-gray-200 tw-bg-white">
						<span class="tw-text-sm tw-font-semibold tw-text-gray-900">2</span>
					</div>
					<div class="tw-flex tw-flex-1 tw-flex-col tw-gap-1">
						<span class="tw-text-xs tw-font-normal tw-text-gray-800">
							{{ $t('integrations.instagram-step-2') }}
						</span>
					</div>
				</div>

				<!-- Step 3 -->
				<div class="tw-flex tw-w-full tw-items-center tw-gap-2.5">
					<div class="tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-gray-200 tw-bg-white">
						<span class="tw-text-sm tw-font-semibold tw-text-gray-900">3</span>
					</div>
					<div class="tw-flex tw-flex-1 tw-flex-col tw-gap-1">
						<span class="tw-text-xs tw-font-normal tw-text-gray-800">
							{{ $t('integrations.instagram-step-3') }}
						</span>
					</div>
				</div>
			</div>

			<!-- Connect Instagram Button -->
			<div class="tw-flex tw-justify-start">
				<AppButton
					size="lg"
					class="instagram-connect-btn"
					@click="startEmbeddedLogin"
					endIcon="instagram-stroke-rounded"
				>
					<span class="tw-pt-0.5 tw-font-medium tw-leading-none tw-text-white">
						{{ $t('integrations.instagram-link-button') }}
					</span>
				</AppButton>
			</div>
		</template>
	</div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { Marketplace } from '../../../../entities/marketplace';
import { useRouter, useRoute } from 'vue-router';
import { api } from '@/v2/plugins/axios';
import { Alert } from '@/v2/plugins/alert';
import { useI18n } from 'vue-i18n';
import { useLayoutStore } from '@/v2/store/shared/layout-store';
import { storeToRefs } from 'pinia';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const layoutStore = useLayoutStore();
const { isRTL } = storeToRefs(layoutStore);

const manualMode = ref(false);
const submitLoading = ref(false);
const form = ref({
	ig_account_id: '',
	ig_access_token: '',
	ig_app_id: '',
	ig_app_secret: '',
});

const onSubmitInstagram = async () => {
	submitLoading.value = true;
	try {
		const success = await Marketplace.saveInstagramManualToken(form.value);
		if (!success) {
			document.location.href = '/store/subscribe/renew_package';
			return;
		} else {
			router.push('/v2/integrations/marketplaces');
		}
	} finally {
		submitLoading.value = false;
	}
};

// Starts the Instagram Embedded OAuth (Business Login) by requesting a server-built URL
const startEmbeddedLogin = async () => {
	try {
		const resp = await api.get('/v1/instagram/embedded-oauth/authorize-url');
		const url = resp?.data?.result?.url || resp?.data?.data?.url || resp?.data?.url;
		if (url) {
			document.location.href = url;
		} else {
			Alert.error(t('integrations.instagram_errors.unknown_error'));
		}
	} catch (e) {
		Alert.error(t('integrations.instagram_errors.unknown_error'));
	}
};

// Show error messages coming from the OAuth callback (?ig_error=...)
onMounted(() => {
	const igError = route.query?.ig_error;
	if (igError) {
		const key = `integrations.instagram_errors.${igError}`;
		const msg = t(key);
		Alert.error(msg || t('integrations.instagram_errors.unknown_error'));
		// Clean the query to avoid showing the message again on refresh
		const { ig_error, ...rest } = route.query;
		router.replace({ path: route.path, query: rest });
	}
});
</script>

<style lang="scss" scoped>
.instagram-connect-btn :deep(.app-button),
.instagram-connect-btn :deep(button),
.instagram-connect-btn.app-button,
.instagram-connect-btn {
	&:is(button) {
		background: linear-gradient(90deg, #F7A054 0%, #C42F8E 51%, #5A56D0 100%) !important;
		background-color: transparent !important;
		border: none !important;
		color: white !important;
		border-radius: 8px !important;
		transition: all 0.3s ease !important;

		&:hover {
			opacity: 0.9 !important;
			transform: translateY(-1px) !important;
			box-shadow: 0 8px 20px rgba(196, 47, 142, 0.3) !important;
		}

		&:active, &:focus {
			opacity: 0.95 !important;
			transform: translateY(0) !important;
		}
	}
}
</style>

<style lang="scss">
/* Unscoped style for maximum specificity to override component styles */
button.instagram-connect-btn,
.instagram-connect-btn button,
.instagram-connect-btn.app-button {
	background: linear-gradient(90deg, #F7A054 0%, #C42F8E 51%, #5A56D0 100%) !important;
	background-color: transparent !important;
	background-image: linear-gradient(90deg, #F7A054 0%, #C42F8E 51%, #5A56D0 100%) !important;
	border: none !important;
	color: white !important;
}

button.instagram-connect-btn:hover,
.instagram-connect-btn button:hover,
.instagram-connect-btn.app-button:hover {
	background: linear-gradient(90deg, #F7A054 0%, #C42F8E 51%, #5A56D0 100%) !important;
	background-color: transparent !important;
	background-image: linear-gradient(90deg, #F7A054 0%, #C42F8E 51%, #5A56D0 100%) !important;
	opacity: 0.9 !important;
	transform: translateY(-1px) !important;
	box-shadow: 0 8px 20px rgba(196, 47, 142, 0.3) !important;
}

button.instagram-connect-btn:active,
button.instagram-connect-btn:focus,
.instagram-connect-btn button:active,
.instagram-connect-btn button:focus,
.instagram-connect-btn.app-button:active,
.instagram-connect-btn.app-button:focus {
	background: linear-gradient(90deg, #F7A054 0%, #C42F8E 51%, #5A56D0 100%) !important;
	background-color: transparent !important;
	background-image: linear-gradient(90deg, #F7A054 0%, #C42F8E 51%, #5A56D0 100%) !important;
}
</style>
